import type { Translation } from './types';
import { hideArrayProperties } from './types';

export const en: Translation = hideArrayProperties<Translation>({
  meta: {
    title: 'Aletiq — Your AI Builds. Aletiq Verifies.',
    description:
      'Your AI agent builds your website and verifies it automatically with Aletiq. Four-eyes principle for AI-generated web apps.',
    ogTitle: 'Aletiq — Your AI Builds. Aletiq Verifies.',
    ogDescription: 'Four-eyes principle for AI-generated web apps. Automatic, secure, zero effort.',
  },
  nav: {
    home: 'Home',
    howItWorks: 'How It Works',
    useCases: 'Use Cases',
    security: 'Security',
    pricing: 'Pricing',
    contact: 'Contact',
    blog: 'Blog',
    cta: 'Get Started',
    languageSwitch: 'DE',
  },

  // ── Hero ────────────────────────────────────────────────────────────────────
  hero: {
    badge: 'For Claude Code, Cursor & Codex via MCP',
    headlineLine1: 'Your AI builds —',
    headlineLine2: 'Aletiq verifies',
    subheadline:
      'Your AI agent writes code faster than you can review. Aletiq verifies automatically, feeds back to the agent, and it fixes issues on the spot — you only see the finished result.',
    ctaPrimary: 'Request Early Access',
    ctaSecondary: 'How It Works',
    chatFlow: [
      { role: 'user', text: 'Build me an online shop with checkout' },
      { role: 'agent', text: 'Building product page, cart and checkout...' },
      { role: 'agent', text: 'aletiq --verify "test the checkout" --page /checkout' },
      { role: 'aletiq', text: 'Test data prepared: product + address filled in', status: 'pass' },
      { role: 'aletiq', text: 'Checkout button not responding on mobile', status: 'fail' },
      { role: 'agent', text: 'Bug detected — fixing touch target...' },
      { role: 'aletiq', text: 'Re-run: Checkout completed successfully', status: 'pass' },
    ],
    demoLines: [
      { type: 'user', text: 'Build me an online shop with checkout' },
      { type: 'blank', text: '' },
      { type: 'agent', text: "I'll build the product page, cart and checkout flow." },
      { type: 'blank', text: '' },
      { type: 'tool-call', text: 'aletiq --verify "test the checkout" --page /checkout' },
      { type: 'blank', text: '' },
      { type: 'pass', text: 'Test data prepared: product + address filled in' },
      { type: 'pass', text: 'Navigation: all links working' },
      { type: 'fail', text: 'Checkout button not responding on mobile' },
      { type: 'blank', text: '' },
      { type: 'agent', text: 'Bug found — fixing touch target on .checkout-btn...' },
      { type: 'blank', text: '' },
      { type: 'tool-call', text: 'aletiq --verify "re-run" (free)' },
      { type: 'blank', text: '' },
      { type: 'pass', text: 'Re-run: Checkout completed successfully' },
      { type: 'pass', text: '3/3 checks passed' },
    ],
  },

  // ── Works With ──────────────────────────────────────────────────────────────
  worksWith: {
    subtitle: 'Integration',
    heading: 'Best with',
    headingHighlight: 'Claude Code',
    description: 'Aletiq uses MCP — the open standard for tool integration. Claude Code has hooks that call Aletiq after every change automatically. Cursor and Codex work via rules.',
    agents: [
      { name: 'Claude Code', logo: '/images/agents/claude-code.svg' },
      { name: 'Cursor', logo: '/images/agents/cursor.svg' },
      { name: 'Codex', logo: '/images/agents/codex.svg' },
      { name: 'Windsurf', logo: '/images/agents/windsurf.svg' },
      { name: 'Cline', logo: '/images/agents/cline.svg' },
      { name: 'Continue', logo: '/images/agents/continue.svg' },
    ],
  },

  // ── How It Works (Homepage short version) ───────────────────────────────────
  howItWorks: {
    label: 'How it works',
    heading: 'How it',
    headingHighlight: 'works',
    intro: 'You describe what you want. Your AI agent builds it, verifies it with Aletiq — and you see the result.',
    steps: [
      {
        number: '01',
        title: 'You describe',
        description: 'Tell your AI agent what you need. In your own words, just like a conversation.',
      },
      {
        number: '02',
        title: 'Your agent builds & verifies',
        description: 'The agent builds your feature, calls Aletiq, gets feedback — and fixes issues on the spot.',
      },
      {
        number: '03',
        title: 'You see the result',
        description: 'Aletiq returns a clear verdict to your agent: passed, attention needed, or failed — right in your workflow.',
      },
    ],
    linkText: 'Learn more',
    linkButton: 'See details',
  },

  // ── Why Aletiq (replaces Verification Levels) ──────────────────────────────
  whyAletiq: {
    label: 'Why Aletiq?',
    heading: 'Why teams',
    headingHighlight: 'choose Aletiq',
    cards: [
      {
        icon: '💻',
        title: 'Runs on your machine',
        description: 'The Aletiq runner tests directly on localhost — zero latency, no deploy needed. Your agent gets feedback in seconds, not minutes.',
      },
      {
        icon: '🔄',
        title: 'Closed feedback loop',
        description: 'Aletiq finds a bug, your agent fixes it immediately — without you lifting a finger. Even test data is prepared automatically.',
      },
      {
        icon: '🔒',
        title: 'Your data stays local',
        description: 'No cloud server sees your code. The runner runs on your machine — that makes Aletiq cheap for us and secure for you.',
      },
      {
        icon: '🚪',
        title: 'It can\'t cheat',
        description: 'Your coding agent has full access — stuck on a broken button, it can reach past the UI and fake a passing test. Aletiq only sees what your users see, through the browser. If it\'s broken, Aletiq can\'t pretend it isn\'t.',
      },
      {
        icon: '🎯',
        title: 'Clear verdicts',
        description: 'Your agent calls aletiq --verify and gets an unambiguous result: passed or failed — with concrete details for the fix.',
      },
    ],
  },

  // ── Use Cases (Homepage cards) ──────────────────────────────────────────────
  useCases: {
    label: 'Use Cases',
    heading: 'Your AI keeps',
    headingHighlight: 'breaking things',
    description: 'Your coding agent builds fast — but it drifts, overwrites, and breaks what worked yesterday. Aletiq catches it before you notice.',
    viewAllButton: 'View all scenarios',
    cards: [
      {
        slug: 'regression',
        icon: '🔄',
        title: 'The agent fixed the cart — and broke the login',
        description: 'Every AI change can break something else. Aletiq re-runs all previous tests after every change — for free.',
      },
      {
        slug: 'drift',
        icon: '🎯',
        title: 'The agent built something different than you asked',
        description: 'You said "add a contact form." The agent built a newsletter signup. Aletiq verifies what was actually built matches your intent.',
      },
      {
        slug: 'silent-fail',
        icon: '👻',
        title: 'It looks fine — but the button does nothing',
        description: 'The page renders perfectly. But the checkout button is dead. No error, no crash — just broken. Aletiq clicks every button and checks if it works.',
      },
      {
        slug: 'overnight',
        icon: '🌙',
        title: 'Your agent coded all night — does any of it work?',
        description: 'You wake up to 15 new features. Aletiq verified each one as it was built. You see the results, not the bugs.',
      },
    ],
  },

  // ── CTA ─────────────────────────────────────────────────────────────────────
  cta: {
    headline: 'Ready?',
    headlineHighlight: 'Start for free',
    button: 'Get Started Free →',
    card: {
      title: 'Start for free',
      description: 'Unlimited checks. Unlimited re-runs. No credit card required.',
      link: 'View all plans →',
    },
  },

  // ── Proof Bar ───────────────────────────────────────────────────────────────
  proofBar: {
    badges: [
      { icon: '🔁', text: 'Re-runs always free' },
      { icon: '🧠', text: 'Muscle memory effect' },
      { icon: '🤖', text: 'Zero manual effort' },
      { icon: '💰', text: 'Up to 91% cheaper' },
    ],
  },

  // ── Muscle Memory ──────────────────────────────────────────────────────────
  muscleMemory: {
    label: 'Muscle Memory',
    heading: 'Test once,',
    headingHighlight: 'protected forever',
    description:
      'Aletiq remembers every test. Every new build is automatically checked against all previous tests — for free. Your safety net grows with every feature, without you doing anything.',
    steps: [
      { number: 'Week 1', label: 'Contact form built', checks: '4 tests' },
      { number: 'Week 2', label: '+ Checkout added', checks: '11 tests' },
      { number: 'Week 4', label: '+ Blog & Dashboard', checks: '31 tests' },
      { number: 'Week 8', label: '+ Payment & Login', checks: '47 re-runs free' },
    ],
    caption: 'New tests cost tokens. All 47 re-runs: $0.',
  },

  // ── Token Savings ──────────────────────────────────────────────────────────
  tokenSavings: {
    label: 'Cost & Efficiency',
    heading: '',
    headingHighlight: '91% cheaper than manual testing',
    description:
      'Your AI agent writes code faster than you can review. Without Aletiq, you test everything manually — or find bugs in production. With Aletiq: automatic, instant, up to 91% cheaper.',
    comparison: [
      { label: 'Manual testing', tokens: 'You review every build yourself', bar: 30 },
      { label: 'No verification', tokens: 'Bugs in production → expensive fix', bar: 15 },
      { label: 'With Aletiq', tokens: 'Automatic, re-runs free', bar: 100, highlight: true },
    ],
    caption: 'Runs locally on your machine + mixed-model strategy (re-runs with fast model) = up to 91% cheaper.',
  },

  // ── Sticky CTA ─────────────────────────────────────────────────────────────
  stickyCta: {
    text: 'Re-runs free',
    button: 'Get Started Free',
  },

  // ── Pricing ─────────────────────────────────────────────────────────────────
  pricing: {
    label: 'Pricing',
    title: 'Simple. Transparent. Fair.',
    popularBadge: 'Most Popular',
    allPlansLabel: 'Included in all plans',
    tiers: [
      {
        name: 'Free',
        price: '$0',
        period: 'forever',
        verifications: 'Unlimited checks',
        features: [
          'Unlimited checks & re-runs',
          'One project',
          'Single-judge verdicts',
          'Bridge (agent asks questions)',
          'CI & API access',
        ],
        cta: 'Get Started Free',
      },
      {
        name: 'Pro',
        price: '$39',
        period: '/month',
        verifications: 'Unlimited checks',
        features: [
          'Everything in Free',
          '3-judge jury (multi-provider consensus)',
          'Up to 10 projects',
          'Priority support',
        ],
        cta: 'Start with Pro',
        popular: true,
      },
      {
        name: 'Premium',
        price: '$89',
        period: '/month',
        verifications: 'Unlimited checks',
        features: [
          'Everything in Pro',
          'Drift detection (is your agent building the right thing?)',
          'Variation generation (1 test becomes 10)',
          'Unlimited projects',
          'Priority queue',
        ],
        cta: 'Start Premium',
      },
    ],
    allPlansInclude: [
      'Unlimited checks & re-runs',
      'Local runner on your machine',
      'MCP integration (Claude Code, Cursor, Codex)',
      'Bridge — your agent asks Aletiq when it gets stuck',
    ],
    faq: {
      label: 'FAQ',
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'Are checks really unlimited?',
          answer: 'Yes. Every plan includes unlimited checks and unlimited re-runs. We can offer this because the Aletiq runner runs on your machine — we only pay for the AI verdict, which costs us fractions of a cent.',
        },
        {
          question: 'What is the 3-judge jury?',
          answer: 'Free uses one AI model to judge your test. Pro uses three different AI models (OpenAI, Google, DeepSeek) and takes a majority vote — significantly more reliable verdicts.',
        },
        {
          question: 'What is drift detection?',
          answer: 'Premium feature: Aletiq checks if what your agent is building still matches your original intent. Catches scope drift before it becomes a problem.',
        },
        {
          question: 'What is variation generation?',
          answer: 'Premium feature: Aletiq automatically generates edge-case variants of your tests — different roles, wrong inputs, mobile viewports — things your agent would never test on its own.',
        },
        {
          question: 'Which AI agents work with Aletiq?',
          answer: 'Claude Code (best integration via MCP + hooks), Cursor (via .cursorrules), Codex (via AGENTS.md), and any MCP-compatible agent.',
        },
        {
          question: 'Do I need a credit card for Free?',
          answer: 'No. The Free plan is free forever, no credit card required.',
        },
      ],
    },
  },

  // ── How It Works (detail page) ──────────────────────────────────────────────
  howItWorksPage: {
    title: 'How Aletiq Works',
    subtitle: 'Your AI agent builds. Aletiq verifies automatically. You see the result.',
    heroLabel: 'How it works',
    heroTitle: 'Your agent builds —',
    heroHighlight: 'Aletiq verifies',
    steps: [
      {
        number: '01',
        title: 'You describe what you want',
        description: 'You talk to your AI agent — not to Aletiq directly. Aletiq works in the background, invisible to you.',
        details: [
          '"Build me an online shop with a shopping cart"',
          '"Add a contact form"',
          '"Create a landing page for my startup"',
        ],
      },
      {
        number: '02',
        title: 'Your agent builds — and verifies automatically',
        description: 'The agent has Aletiq built in as a tool. After every feature, it calls aletiq --verify. Aletiq tests, prepares test data, and gives feedback — the agent fixes issues on the spot.',
        details: [
          'Closed loop: verify \u2192 feedback \u2192 fix \u2192 re-verify',
          'Test data prepared automatically (forms, logins, orders)',
          'No intervention needed — agent and Aletiq handle it together',
        ],
      },
      {
        number: '03',
        title: 'You see clear results',
        description: 'Passed, attention needed, or failed — with concrete details on what was checked. Your agent can react immediately.',
        details: [
          'Passed: Everything works as intended',
          'Attention: Something looks suspicious (e.g. slow loading)',
          'Failed: Something doesn\u2019t work — Aletiq tells your agent exactly what',
        ],
      },
    ],
    resultLabels: {
      pass: 'Passed',
      warn: 'Attention',
      fail: 'Failed',
    },
  },

  // ── Use Cases (detail page) ─────────────────────────────────────────────────
  useCasesPage: {
    title: 'What Aletiq Checks For You',
    subtitle: 'No matter what your AI agent builds \u2014 Aletiq checks if it works.',
    heroLabel: 'Use Cases',
    heroTitle: 'No matter what your agent builds \u2014',
    heroHighlight: 'Aletiq checks it',
    terminalLabel: 'Live Example',
    terminalTitle: 'This is what a check looks like',
    terminalDescription: 'Your agent calls aletiq --verify. Aletiq prepares test data, checks everything, and gives concrete feedback.',
    scenariosLabel: 'Scenarios',
    scenariosTitle: 'What your agent builds \u2014',
    scenariosHighlight: 'Aletiq checks it',
    cards: [
      {
        slug: 'online-shop',
        icon: '🛒',
        title: 'Online Shop',
        whatYouSay: 'Build me a shop with a shopping cart',
        whatAletiqChecks: 'Can customers add products and complete checkout?',
      },
      {
        slug: 'landing-page',
        icon: '🚀',
        title: 'Landing Page',
        whatYouSay: 'Create a landing page for my startup',
        whatAletiqChecks: 'Do all links work? Does it look good on mobile?',
      },
      {
        slug: 'booking-system',
        icon: '📅',
        title: 'Booking System',
        whatYouSay: 'I need an appointment booking tool',
        whatAletiqChecks: 'Can users select and confirm appointments?',
      },
      {
        slug: 'dashboard',
        icon: '📊',
        title: 'Dashboard',
        whatYouSay: 'Build me a customer dashboard',
        whatAletiqChecks: 'Does the data load? Does the navigation work?',
      },
      {
        slug: 'blog',
        icon: '📝',
        title: 'Blog',
        whatYouSay: 'Create a blog with comments',
        whatAletiqChecks: 'Can visitors read posts and leave comments?',
      },
      {
        slug: 'member-area',
        icon: '🔐',
        title: 'Member Area',
        whatYouSay: 'Build a login area for my customers',
        whatAletiqChecks: 'Does registration, login, and password reset work?',
      },
    ],
  },

  // ── Security Page (replaces Architecture) ───────────────────────────────────
  securityPage: {
    title: 'Security & Privacy',
    subtitle: 'Your data stays with you. Always.',
    heroLabel: 'Security',
    heroTitle: 'Your data stays',
    heroHighlight: 'with you. Always.',
    architectureLabel: 'Architecture',
    architectureTitle: 'Everything runs on your machine',
    architectureDescription: 'The Aletiq runner runs locally on your machine. Your code, data, and tests never leave your network. Only anonymized usage statistics reach our servers.',
    points: [
      {
        icon: '🏠',
        title: 'Your data stays with you',
        description: 'All tests run on your machine. Your code and data never leave your network.',
      },
      {
        icon: '👁️',
        title: 'We don\u2019t see your code',
        description: 'Only test results and observations are analyzed. Your source code stays private.',
      },
      {
        icon: '🤖',
        title: 'Your agent, your control',
        description: 'Aletiq only does what your agent asks. No autonomous actions, no hidden access.',
      },
      {
        icon: '🔒',
        title: 'Encrypted communication',
        description: 'All connections are encrypted. Your data is just as safe in transit as it is at rest.',
      },
    ],
  },

  // ── Contact ─────────────────────────────────────────────────────────────────
  contact: {
    title: 'Contact',
    subtitle: 'Write to us — we\u2019ll get back to you quickly',
    nameLabel: 'Name',
    emailLabel: 'Email',
    companyLabel: 'Company',
    messageLabel: 'Message',
    submitButton: 'Send Message',
    salesHeading: 'Questions about Premium or Team?',
    validation: {
      nameMin: 'Name must be at least 2 characters',
      emailInvalid: 'Please enter a valid email address',
      messageMin: 'Message must be at least 10 characters',
    },
    status: {
      success: 'Your message has been sent. We\u2019ll get back to you soon!',
      error: 'Something went wrong. Please try again later.',
    },
  },
  earlyAccess: {
    title: 'Request Early Access',
    subtitle: 'Aletiq is in early access. We onboard design partners manually to make sure the product fits the way you build.',
    badge: 'Early Access',
    intro: 'Tell us what you’re building and how you verify it today. If there’s a fit, we’ll be in touch within 2 business days with a private setup.',
    trustBullets: [
      { icon: '•', text: 'No credit card required' },
      { icon: '•', text: 'Limited slots — currently onboarding design partners' },
      { icon: '•', text: 'Reply within 2 business days' },
    ],
    form: {
      nameLabel: 'Your name',
      emailLabel: 'Work email',
      emailHint: 'Use a work email — helps us prioritise.',
      companyLabel: 'Company',
      stackLabel: 'Stack / framework',
      stackPlaceholder: 'e.g. Next.js + Vercel, SvelteKit, Rails',
      goalLabel: 'What do you want to verify?',
      goalPlaceholder: 'Briefly: what flows does your AI agent build, and where does it currently break? (1–2 sentences)',
      githubLabel: 'GitHub handle (optional)',
      githubHint: 'Optional — helps us understand your work.',
      submitButton: 'Request access',
      submittingButton: 'Submitting…',
    },
    validation: {
      nameMin: 'Please enter your name',
      emailInvalid: 'Please enter a valid work email',
      stackMin: 'Please tell us your stack',
      goalMin: 'Please describe what you want to verify (at least 10 characters)',
    },
    status: {
      successTitle: 'Got it — thanks!',
      successDetail: 'We’ll review your request and reply within 2 business days. Check your inbox (and spam folder) for a confirmation.',
      error: 'Something went wrong. Please try again or email us at hello@aletiq.ai.',
    },
  },
  thankYou: {
    title: 'Thank you \u2014 message received.',
    subtitle: "We'll be in touch within 1\u20132 business days. Check your inbox for a confirmation.",
    backHome: 'Back to Home',
  },
  notFound: {
    title: 'Page Not Found',
    description: 'The page you\u2019re looking for doesn\u2019t exist or has been moved.',
    backHome: 'Back to Home',
  },
  footer: {
    copyright: '© 2026 Aletiq. All rights reserved.',
    builtBy: 'Built by Denqfabrik',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    imprint: 'Imprint',
  },
  structuredData: {
    description:
      'Aletiq automatically verifies that AI-generated web apps work correctly. Four-eyes principle for AI agents — secure, fast, zero effort.',
    offerDescriptions: [
      'Automatic quality checks for AI-generated websites',
      'Four-eyes principle: Your agent builds, Aletiq verifies',
      'Real browser tests with clear verdicts for your agent',
    ],
  },
});
